/**
 * Cuentas de Gavi inteligente (Vadik, 2-nov-2026: «no calcula ni proyecta»).
 * Todo puro y en céntimos por dentro; devuelve euros. Claude nunca hace una
 * cuenta de cabeza: pide `calcular` o una de las herramientas que ya traen el
 * total (patrimonio, fin de una deuda, ritmo del patrimonio).
 */
import { integerToAmount } from '@actual-app/core/shared/util';

import {
  grupoPatrimonioDe,
  gruposPatrimonio,
} from '#components/mobile/accounts/cuentas';
import type {
  GrupoPatrimonio,
  TipoCuenta,
} from '#components/mobile/accounts/cuentas';
import {
  calcularAmortizacion,
  MAX_MESES,
  sumarMeses,
} from '#components/mobile/deudas/amortizacion';
import { mediana } from '#components/mobile/informes/estadisticaRobusta';

/** Euros con dos decimales a partir de céntimos. */
export function euros(centimos: unknown): number {
  const n = typeof centimos === 'number' ? centimos : 0;
  return Math.round(integerToAmount(n) * 100) / 100;
}

// --- calcular ---

const MAX_EXPRESION = 200;
const DECIMALES = 1e6;

/**
 * Aritmética exacta para Claude: números con punto decimal, + - * / ^ y
 * paréntesis. Analizador propio (sin `eval`): `evalArithmetic` del motor lee
 * los números según el formato de la app y con miles en punto tomaría
 * «0.0699» por 699. Lanza un error que Claude entiende si algo no cuadra.
 */
export function calcular(expresion: unknown): number {
  if (typeof expresion !== 'string' || !expresion.trim()) {
    throw new Error('falta la expresión');
  }
  if (expresion.length > MAX_EXPRESION) {
    throw new Error(`expresión de más de ${MAX_EXPRESION} caracteres`);
  }
  if (expresion.includes(',')) {
    throw new Error('usa punto para los decimales y ningún separador de miles');
  }
  // Los signos que Claude copia de las fórmulas en llano.
  const ascii = expresion
    .replace(/×/g, '*')
    .replace(/÷/g, '/')
    .replace(/−/g, '-');
  const fichas = ascii.match(/\d+(?:\.\d+)?|[-+*/^()]|\S/g) ?? [];
  let i = 0;
  const ver = () => fichas[i];
  const tomar = (f: string) => {
    if (fichas[i] !== f) {
      throw new Error(`se esperaba «${f}»`);
    }
    i++;
  };

  function suma(): number {
    let v = producto();
    while (ver() === '+' || ver() === '-') {
      v = fichas[i++] === '+' ? v + producto() : v - producto();
    }
    return v;
  }
  function producto(): number {
    let v = signo();
    while (ver() === '*' || ver() === '/') {
      if (fichas[i++] === '*') {
        v *= signo();
      } else {
        const d = signo();
        if (d === 0) {
          throw new Error('división por cero');
        }
        v /= d;
      }
    }
    return v;
  }
  function signo(): number {
    if (ver() === '-' || ver() === '+') {
      return fichas[i++] === '-' ? -signo() : signo();
    }
    return potencia();
  }
  function potencia(): number {
    const base = numero();
    if (ver() === '^') {
      i++;
      return Math.pow(base, signo());
    }
    return base;
  }
  function numero(): number {
    const f = ver();
    if (f === '(') {
      i++;
      const v = suma();
      tomar(')');
      return v;
    }
    if (f !== undefined && /^\d/.test(f)) {
      i++;
      return Number(f);
    }
    throw new Error(
      f === undefined ? 'la expresión acaba a medias' : `«${f}» no vale aquí`,
    );
  }

  const resultado = suma();
  if (i < fichas.length) {
    throw new Error(`«${fichas[i]}» no vale aquí`);
  }
  if (!Number.isFinite(resultado) || Math.abs(resultado) > 1e15) {
    throw new Error('el resultado no es un número manejable');
  }
  return Math.round(resultado * DECIMALES) / DECIMALES;
}

// --- patrimonio neto ---

export type CuentaConTipo = {
  nombre: string;
  tipo: TipoCuenta;
  /** Céntimos. */
  saldo: number;
};

/** Lo que tienes menos lo que debes, con los grupos de la pestaña Cuentas. */
export function patrimonio(cuentas: readonly CuentaConTipo[]) {
  const conSaldo = cuentas.filter(c => c.saldo !== 0);
  const tienes = conSaldo
    .filter(c => c.saldo > 0)
    .reduce((s, c) => s + c.saldo, 0);
  const debes = conSaldo
    .filter(c => c.saldo < 0)
    .reduce((s, c) => s - c.saldo, 0);
  const porGrupo = (grupo: GrupoPatrimonio) =>
    conSaldo
      .filter(c => grupoPatrimonioDe(c) === grupo)
      .sort((a, b) => Math.abs(b.saldo) - Math.abs(a.saldo))
      .map(c => ({ cuenta: c.nombre, saldo: euros(c.saldo) }));
  return {
    patrimonioNeto: euros(tienes - debes),
    loQueTienes: euros(tienes),
    loQueDebes: euros(debes),
    formula: 'lo que tienes − lo que debes',
    grupos: gruposPatrimonio(conSaldo).map(g => ({
      grupo: g.grupo,
      total: euros(g.grupo === 'deudas' ? -g.valor : g.valor),
      cuentas: porGrupo(g.grupo),
    })),
  };
}

// --- fin de una deuda ---

export type DatosFinDeuda = {
  nombre: string;
  /** Lo que se debe, en céntimos y en positivo. */
  saldo: number;
  /** TIN en %; null si la ficha de la deuda no lo tiene. */
  interesAnual: number | null;
  /** Cuota de la ficha, en céntimos; null si no la tiene. */
  cuota: number | null;
  /** Lo pagado en cada uno de los últimos meses completos, en céntimos. */
  pagosRecientes: readonly number[];
  /** Mes del próximo pago, AAAA-MM. */
  mesProximoPago: string;
  /** Céntimos de más al mes (simulación). */
  extra: number;
};

/** Meses que faltan y mes del último pago de una deuda. */
export function finDeDeuda(d: DatosFinDeuda) {
  const comun = { cuenta: d.nombre, debes: euros(d.saldo) };
  if (d.saldo <= 0) {
    return { ...comun, estado: 'pagada' as const };
  }
  // Sin cuota en la ficha: la mediana de lo pagado, para que un abono suelto
  // no la infle.
  const pagos = d.pagosRecientes.filter(p => p > 0);
  const cuota = d.cuota ?? (pagos.length > 0 ? mediana(pagos) : null);
  if (!cuota) {
    return {
      ...comun,
      estado: 'sin cuota' as const,
      nota: 'Sin cuota en la ficha de la deuda ni pagos en los últimos meses: no hay con qué calcular. Se puede apuntar la cuota en la ficha de la deuda.',
    };
  }
  const extra = Math.max(0, d.extra);
  const pago = cuota + extra;
  const a = calcularAmortizacion(
    d.saldo,
    d.interesAnual ?? 0,
    pago,
    d.mesProximoPago,
  );
  const resto = {
    ...comun,
    cuotaUsada: euros(cuota),
    origenCuota:
      d.cuota != null
        ? 'la ficha de la deuda'
        : `estimada con lo pagado en los últimos ${pagos.length} meses (mediana)`,
    extraMensual: euros(extra),
    interesAnual: d.interesAnual,
  };
  if (a.noAmortiza) {
    return {
      ...resto,
      estado: 'no se acaba' as const,
      nota: 'Con esa cuota no se cubre ni el interés de cada mes: la deuda no baja.',
    };
  }
  return {
    ...resto,
    estado: 'calculada' as const,
    meses: a.mesesRestantes,
    ultimoPago: a.fechaFin,
    interesesQueFaltan: euros(a.interesTotal),
    formula:
      d.interesAnual != null
        ? 'cuadro de amortización (método francés) con el interés de la ficha'
        : 'lo que debes ÷ la cuota',
    ...(d.interesAnual == null
      ? {
          nota: 'Sin interés apuntado en la ficha: la cuenta es solo saldo ÷ cuota; con intereses tardaría algo más.',
        }
      : {}),
  };
}

// --- ritmo del patrimonio ---

/** Meses hasta llegar a cero a ese ritmo; null si no llega (o pasa de 100 años). */
function mesesHastaCero(neto: number, ritmo: number): number | null {
  if (neto === 0) {
    return 0;
  }
  const haciaCero = (neto < 0 && ritmo > 0) || (neto > 0 && ritmo < 0);
  if (!haciaCero) {
    return null;
  }
  // Más de 100 años, como en la ficha de la deuda, no es una fecha.
  const meses = Math.ceil(Math.abs(neto / ritmo));
  return meses <= MAX_MESES ? meses : null;
}

function estadoRitmo(
  neto: number,
  meses: number | null,
):
  | 'ya está a cero'
  | 'no llega a cero a este ritmo'
  | 'llega a cero'
  | 'se queda a cero' {
  if (neto === 0) {
    return 'ya está a cero';
  }
  if (meses == null) {
    return 'no llega a cero a este ritmo';
  }
  return neto < 0 ? 'llega a cero' : 'se queda a cero';
}

/**
 * Cuánto cambia el patrimonio al mes (mediana de los meses dados, para que
 * un mes raro no tuerza la cuenta) y, a ese ritmo, cuándo llegaría a cero.
 * Los meses antes del primer movimiento no cuentan (presupuesto nuevo).
 */
export function ritmoPatrimonio(
  cambios: readonly { mes: string; cambio: number }[],
  meses: readonly string[],
  neto: number,
  mesActual: string,
) {
  const porMes = new Map(cambios.map(c => [c.mes, c.cambio]));
  const primero = meses.findIndex(mes => porMes.has(mes));
  const serie = (primero < 0 ? [] : meses.slice(primero)).map(mes => ({
    mes,
    cambio: porMes.get(mes) ?? 0,
  }));
  const valores = serie.map(s => s.cambio);
  const ritmo = mediana(valores);
  const media = valores.length
    ? Math.round(valores.reduce((s, v) => s + v, 0) / valores.length)
    : 0;
  const n = mesesHastaCero(neto, ritmo);
  return {
    patrimonioNeto: euros(neto),
    ritmoMensual: euros(ritmo),
    media: euros(media),
    serie: serie.map(s => ({ mes: s.mes, cambio: euros(s.cambio) })),
    formula: 'patrimonio neto ÷ ritmo mensual (la mediana de los meses)',
    estado: estadoRitmo(neto, n),
    meses: n,
    mesAproximado: n == null || n === 0 ? null : sumarMeses(mesActual, n),
  };
}
